/**
 * AsyncStatusToggle — toggle přepínač s optimistic update, rollback a
 * syncing indikací pro asynchronní operace kde serverový stav se
 * aktualizuje mimo request/response cyklus (publish, reconcile, ...).
 *
 * Chování:
 * ────────
 *   1. Klik → toggle se OKAMŽITĚ vizuálně přepne (optimistic), spustí onToggle.
 *   2. Během pendingu: toggle ukazuje new state, badge "zpracovává…" (amber).
 *   3. Success:
 *        - Optimistic SE NEMAŽE hned — čeká se až server (přes useLiveProp
 *          nebo refetch) dodá nová data a `value` dohne optimistic.
 *        - Toggle zůstane vizuálně v new state (bez "flick" zpět na old).
 *        - Jakmile value === optimistic, optimistic se vyčistí, badge zmizí.
 *   4. Error:
 *        - Rollback IHNED (optimistic = null → toggle zpět na původní).
 *        - Badge "zpracovává…" NAHRAZEN red badge s konkrétní chybou.
 *        - Auto-clear po 8s nebo při příštím kliku.
 *
 * Kontrakt:
 * ─────────
 *   value    = desired state (z DB, okamžitě uložené)
 *   actual   = actual state (živý svět — NPM host, reconcile, ...)
 *   onToggle = async mutation
 *
 * Companion: useLiveProp — pro modaly co dostávají item propem.
 * Viz wiki: ui-pattern-async-toggle, ui-pattern-live-prop.
 */
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

export interface AsyncStatusToggleProps {
  /** Desired state z DB (zdroj pravdy po dokončení operace) */
  value: boolean;
  /**
   * Actual state — reálný stav světa. Když undefined, syncing se řídí jen
   * podle pendingu a rozdílu value vs optimistic.
   */
  actual?: boolean;
  /**
   * Klik → onToggle(newValue). Musí vrátit Promise (nebo awaited) — při
   * reject se toggle vrátí na původní a chyba se zobrazí v badge.
   */
  onToggle: (newValue: boolean) => Promise<unknown> | void;
  /** Callback při erroru mutace — typicky toast */
  onError?: (err: unknown) => void;

  /** Popisek vedle toggle */
  label?: ReactNode;
  /** Ikona vlevo — dostává aktuální displayed stav */
  renderIcon?: (on: boolean) => ReactNode;
  /** Badge vpravo když není syncing (ani error) — např. "publikováno" */
  renderIdleBadge?: (on: boolean) => ReactNode;
  /** Vlastní text syncing badge (default: "zpracovává…") */
  syncingLabel?: string;
  /** Jak dlouho po chybě držet error badge (ms, default 8000) */
  errorDisplayMs?: number;

  /** Externí pending (např. z useMutation.isPending) — překreslí jako syncing */
  pending?: boolean;
  /** Disabled bez pending animace */
  disabled?: boolean;

  className?: string;
}

export function AsyncStatusToggle({
  value,
  actual,
  onToggle,
  onError,
  label,
  renderIcon,
  renderIdleBadge,
  syncingLabel = 'zpracovává…',
  errorDisplayMs = 8000,
  pending = false,
  disabled = false,
  className = '',
}: AsyncStatusToggleProps) {
  const { t } = useTranslation();
  /**
   * Optimistic local state.
   *   null  = žádná lokální override, displayed se řídí propem `value`.
   *   true  = čeká se na potvrzení "zapnuto" od serveru.
   *   false = čeká se na potvrzení "vypnuto" od serveru.
   */
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const [localPending, setLocalPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Když server dohne optimistic (value === optimistic), vyčisti optimistic.
   * Tímto se "flick" odstraní — optimistic držel new state až do chvíle, kdy
   * prop dohne, potom předá řízení propu.
   */
  useEffect(() => {
    if (optimistic !== null && value === optimistic) {
      setOptimistic(null);
    }
  }, [value, optimistic]);

  /** Auto-clear error badge po N ms */
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), errorDisplayMs);
    return () => clearTimeout(t);
  }, [error, errorDisplayMs]);

  // Publish může dojít mimo tuhle mutaci (CLI). Jakmile server dojde, starou
  // chybu z předchozího kliku už nedrž.
  useEffect(() => {
    if (error && actual !== undefined && value === actual) setError(null);
  }, [error, value, actual]);

  // Displayed: optimistic má přednost (ihned po kliku), jinak prop value.
  const displayed = optimistic ?? value;

  // Syncing: externí pending, lokální pending, NEBO value != actual
  // (server má desired ale NPM/svět ještě nedohnal). NEBO optimistic != value
  // (čekáme že prop dohne optimistic).
  const outOfSync = actual !== undefined && value !== actual;
  const waitingForProp = optimistic !== null && value !== optimistic;
  const syncing = pending || localPending || outOfSync || waitingForProp;

  async function handleClick() {
    if (disabled || pending || localPending) return;
    const newValue = !displayed;
    setOptimistic(newValue);
    setError(null);
    setLocalPending(true);
    try {
      await onToggle(newValue);
      // Úspěch: optimistic zůstává dokud value nedohne (useEffect to vyčistí).
      // Tím toggle neflickne na staré value.
    } catch (err) {
      // Chyba: rollback — optimistic se vymaže, toggle padne zpět na value.
      setOptimistic(null);
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError?.(err);
    } finally {
      setLocalPending(false);
    }
  }

  const isDisabled = disabled || pending || localPending;

  return (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      <div className='flex items-center gap-2 min-w-0'>
        {renderIcon && renderIcon(displayed)}
        {label && <span className='text-sm text-gray-300 truncate'>{label}</span>}
        {error
          ? (
            <span
              className='text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 inline-flex items-center gap-1 max-w-[220px] truncate'
              title={error}
            >
              {error}
            </span>
          )
          : syncing
          ? (
            <span
              className='text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 animate-pulse inline-flex items-center gap-1'
              title={outOfSync
                ? t('ui.pendingWorldSync')
                : waitingForProp
                ? t('ui.pendingServer')
                : t('ui.mutationInProgress')}
            >
              <Loader2 size={9} className='animate-spin' />
              {syncingLabel}
            </span>
          )
          : (
            renderIdleBadge?.(displayed)
          )}
      </div>
      <button
        type='button'
        role='switch'
        onClick={handleClick}
        disabled={isDisabled}
        aria-checked={displayed}
        aria-pressed={displayed}
        className={`relative w-11 h-6 rounded-full transition-all duration-150 flex-shrink-0 ${
          displayed ? 'bg-plex acm-toggle-track-on' : 'acm-toggle-track'
        } ${isDisabled ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
      >
        <div
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white acm-toggle-knob transition-transform duration-150 ${
            displayed ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </div>
  );
}
